import type { PurchasedProduct } from "@/hooks/usePurchasedProducts";
import type { Variant } from "@/hooks/usePurchasedProductDetail";
import { groupSubscriptions } from "shared/subscriptions";
import {
  computeSizeUpBundle,
  parsePackCount,
  parseServings,
  subscriptionPromoPercent,
  type SizeUpBundle,
} from "./cancel-chat-bundle";
import { isActiveSubscription } from "./subscriptionEligibility";
import { contractPerDeliveryTotal } from "./subscription-pricing";
import type { ManageSubscriptionSheetProduct } from "./types";

/** Titles that opt a product into the size-up offer (Rose Nectar only). */
const ROSE_NECTAR_TITLE = /rose\s*nectar/i;

export function isRoseNectarTitle(title: string | null | undefined): boolean {
  return !!title && ROSE_NECTAR_TITLE.test(title);
}

/**
 * Ladder SKUs that can size up today: 32 servings 1-Pack or 64 servings 1-Pack.
 * Top of ladder (64 servings 3-Pack) has no larger target.
 */
export function isBundleEligibleVariant(
  variantTitle: string | null | undefined,
): boolean {
  const servings = parseServings(variantTitle);
  if (servings == null) return false;
  const packs = parsePackCount(variantTitle);
  // Top of ladder — nothing larger on the known Rose Nectar size-up ladder.
  if (servings === 64 && packs >= 3) return false;
  return (
    (servings === 32 && packs === 1) || (servings === 64 && packs === 1)
  );
}

/** One active, single-line Rose Nectar subscription that could size up. */
export interface SizeUpCandidate {
  subscriptionId: number;
  /** The single line item on the contract. */
  product: PurchasedProduct;
  nextBillingDate: string | null;
}

/** Banner chip for the size-up offer. */
export interface SizeUpChip {
  subscriptionId: number;
  title: string;
  imageUrl: string | null;
  nextBillingDate: string | null;
}

/** A fully computed size-up offer ready for the banner / review dialog. */
export interface SizeUpOffer {
  candidate: SizeUpCandidate;
  bundle: SizeUpBundle;
  chip: SizeUpChip;
}

/**
 * Prefilter manage products into single-line Rose Nectar ladder candidates.
 * Does not run pricing math — callers fetch product detail + shipping, then
 * {@link pickBestSizeUpOffer} / {@link computeSizeUpOfferForInput}.
 */
export function getSizeUpCandidates(
  products: PurchasedProduct[] | null | undefined,
): SizeUpCandidate[] {
  if (!products?.length) return [];
  const out: SizeUpCandidate[] = [];
  for (const group of groupSubscriptions(products)) {
    if (group.subscriptionId == null) continue;
    if (group.products.length !== 1) continue;
    const product = group.products[0];
    if (!product || !isActiveSubscription(product)) continue;
    if (product.subscription_cancel_scheduled_at) continue;
    if (!isRoseNectarTitle(product.title)) continue;
    if (!isBundleEligibleVariant(product.variant_title)) continue;
    out.push({
      subscriptionId: group.subscriptionId,
      product,
      nextBillingDate: product.subscription_next_billing_date ?? null,
    });
  }
  return out;
}

/** Inputs needed to run {@link computeSizeUpBundle} for one candidate. */
export interface SizeUpComputeInput {
  candidate: SizeUpCandidate;
  variants: Variant[];
  currentVariantId: number | null;
  currentVariantTitle: string | null;
  /**
   * Previewed per-delivery shipping for the TARGET variant. Null omits shipping
   * from both sides (same as cancel-chat). Pass 0 optimistically when a fee is
   * expected to clear free-shipping thresholds.
   */
  targetShippingCost: number | null;
}

function toChip(candidate: SizeUpCandidate): SizeUpChip {
  const { product, subscriptionId, nextBillingDate } = candidate;
  return {
    subscriptionId,
    title: product.title,
    imageUrl: product.image_url ?? null,
    nextBillingDate,
  };
}

/**
 * Compute a size-up offer for one candidate, or null when it doesn't qualify.
 * Always uses `subReason: null` (default non–budget-tight picker).
 */
export function computeSizeUpOfferForInput(
  input: SizeUpComputeInput,
): SizeUpOffer | null {
  const { candidate, variants, currentVariantId, currentVariantTitle, targetShippingCost } =
    input;
  const { product } = candidate;
  const cadence =
    product.subscription_billing_interval_count != null &&
    product.subscription_billing_interval != null
      ? {
          interval: product.subscription_billing_interval_count,
          intervalUnit: product.subscription_billing_interval,
        }
      : null;

  const bundle = computeSizeUpBundle({
    productTitle: product.title,
    isSingleLine: true,
    cadence,
    currentVariantId,
    currentVariantTitle:
      currentVariantTitle ?? product.variant_title ?? null,
    variants,
    contractPerDeliveryTotal: contractPerDeliveryTotal([
      product as ManageSubscriptionSheetProduct,
    ]),
    currentShippingCost: product.subscription_shipping_price ?? null,
    targetShippingCost,
    discountPercent: subscriptionPromoPercent(product.subscription_discounts),
    subReason: null,
  });
  if (!bundle) return null;
  return { candidate, bundle, chip: toChip(candidate) };
}

/**
 * Among computed inputs, return the offer with the largest `savingPerMonth`,
 * or null when none qualify.
 */
export function pickBestSizeUpOffer(
  inputs: SizeUpComputeInput[],
): SizeUpOffer | null {
  let best: SizeUpOffer | null = null;
  for (const input of inputs) {
    const offer = computeSizeUpOfferForInput(input);
    if (!offer) continue;
    if (
      best == null ||
      offer.bundle.savingPerMonth > best.bundle.savingPerMonth
    ) {
      best = offer;
    }
  }
  return best;
}

/**
 * Manage-tab banner exclusivity: when size-up is showing, hide merge.
 * Deep links are handled separately and may still open either dialog.
 */
export function resolveManageOfferBanners(args: {
  sizeUpEligible: boolean;
  sizeUpSuppressed: boolean;
  mergeEligible: boolean;
  mergeSuppressed: boolean;
}): { showSizeUpBanner: boolean; showMergeBanner: boolean } {
  const showSizeUpBanner = args.sizeUpEligible && !args.sizeUpSuppressed;
  const showMergeBanner =
    args.mergeEligible && !args.mergeSuppressed && !showSizeUpBanner;
  return { showSizeUpBanner, showMergeBanner };
}
